import styled from 'styled-components';

export const PanelContainer = styled.div<{ $disabled?: boolean; $isDragActive?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 374px;
  min-width: 374px;
  height: 100%;
  background-color: ${({ theme }) => (theme as any).color.ui6};
  border-left: 1px solid ${({ theme }) => (theme as any).color.ui5};
  overflow: hidden;
  box-sizing: border-box;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  flex-shrink: 0;
  border-bottom: 1px solid ${({ theme }) => (theme as any).color.ui5};
`;

export const PanelTitle = styled.h3`
  ${({ theme }) => (theme as any).font.t1};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => (theme as any).color.ui2};
  margin: 0;
`;

export const PanelBody = styled.div<{ $dimmed?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  padding: 14px 14px 14px 14px;
  gap: 14px;
  min-height: 0;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.5 : 1)};
  transition: opacity 0.15s ease;
`;

export const DropzoneOverlay = styled.div`
  ${({ theme }) => (theme as any).font.t3};
  color: ${({ theme }) => (theme as any).color.ui2};
  font-weight: 400;
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.6);
  pointer-events: none;
  z-index: 10;
  padding: 24px;
  text-align: center;
`;

export const GraphicsContainer = styled.div<{ $itemCount: number }>`
  display: grid;
  grid-template-columns: repeat(4, 75px);
  grid-auto-rows: 80px;
  gap: 8px;
  width: 100%;
  max-height: ${({ $itemCount }) => ($itemCount > 7 ? '166px' : 'fit-content')};
  overflow-y: auto;
  box-sizing: border-box;
  flex-shrink: 0;
  scrollbar-gutter: stable;
  scrollbar-color: ${({ theme }) => (theme as any).color.ui4} transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => (theme as any).color.ui4};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => (theme as any).color.ui3};
  }
`;

export const ThumbnailActionButton = styled.button`
  position: absolute;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => (theme as any).color.ui4};
  background-color: ${({ theme }) => (theme as any).color.ui6};
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  padding: 0;

  svg {
    width: 10px;
    height: 10px;
    color: ${({ theme }) => (theme as any).color.ui3};
  }

  &:hover {
    border-color: ${({ theme }) => (theme as any).color.s2};
    svg {
      color: ${({ theme }) => (theme as any).color.s2};
    }
  }
`;

export const DeleteButton = styled(ThumbnailActionButton)`
  top: -2px;
  right: -2px;
`;

export const ReplaceButton = styled(ThumbnailActionButton)`
  top: -2px;
  left: -2px;
`;

export const GraphicThumbnail = styled.div<{
  $selected?: boolean;
  $isDragAccept?: boolean;
  $isDragReject?: boolean;
  $showDropHint?: boolean;
}>`
  position: relative;
  width: 70px;
  height: 70px;
  border-radius: 6px;
  border: 2px
    ${({ $selected, $isDragAccept, $isDragReject, $showDropHint, theme }) => {
      const t = theme as any;
      if ($isDragReject) return `dashed ${t.color.ac12 ?? '#e44'}`;
      if ($isDragAccept) return `dashed ${t.color.s2}`;
      if ($showDropHint) return `dashed ${t.color.s1}`;
      return $selected ? `solid ${t.color.s1}` : `solid ${t.color.ui5}`;
    }};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $isDragReject }) => ($isDragReject ? 'not-allowed' : 'pointer')};
  box-shadow: ${({ theme }) => (theme as any).shadows.outerShadow1};
  overflow: visible;
  background-image: linear-gradient(45deg, #ccc 25%, transparent 25%),
    linear-gradient(-45deg, #ccc 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #ccc 75%),
    linear-gradient(-45deg, transparent 75%, #ccc 75%);
  background-size: 12px 12px;
  background-position: 0 0, 0 6px, 6px -6px, -6px 0px;
  background-color: #ffffff;

  > img {
    max-width: 88%;
    max-height: 88%;
    object-fit: contain;
    border-radius: 3px;
  }

  &:hover {
    border-color: ${({ $isDragReject, theme }) => {
      const t = theme as any;
      return $isDragReject ? t.color.ac12 ?? '#e44' : t.color.s2;
    }};

    ${DeleteButton} {
      display: flex;
    }
    ${ReplaceButton} {
      display: flex;
    }
  }
`;

export const LoadingThumbnail = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 6px;
  border: 2px solid ${({ theme }) => (theme as any).color.ui5};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  box-shadow: ${({ theme }) => (theme as any).shadows.outerShadow1};
  background-color: ${({ theme }) => (theme as any).color.ui6};
  flex-shrink: 0;
`;

export const LoadingLabel = styled.span`
  font-size: 9px;
  color: ${({ theme }) => (theme as any).color.ui3};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 4px;
  text-align: center;
`;

export const ThumbnailLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 4px;
  z-index: 5;
`;

export const AddGraphicButton = styled.div<{
  $isDragAccept?: boolean;
  $isDragReject?: boolean;
  $showDropHint?: boolean;
}>`
  width: 70px;
  height: 70px;
  border-radius: 6px;
  border-style: dashed;
  border-width: 2px;
  border-color: ${({ $isDragAccept, $isDragReject, $showDropHint, theme }) => {
    const t = theme as any;
    if ($isDragReject) return t.color.ac12 ?? '#e44';
    if ($isDragAccept) return t.color.s2;
    if ($showDropHint) return t.color.s1;
    return t.color.s1;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $isDragReject }) => ($isDragReject ? 'not-allowed' : 'pointer')};
  box-shadow: ${({ theme }) => (theme as any).shadows.outerShadow1};
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => (theme as any).color.s1};
  }

  &:hover {
    border-color: ${({ $isDragReject, theme }) => {
      const t = theme as any;
      return $isDragReject ? t.color.ac12 ?? '#e44' : t.color.s2;
    }};
    svg {
      color: ${({ theme }) => (theme as any).color.s2};
    }
  }
`;

export const PropertiesContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  min-width: 0;
  scrollbar-gutter: stable;
  scrollbar-color: ${({ theme }) => (theme as any).color.ui4} transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => (theme as any).color.ui4};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => (theme as any).color.ui3};
  }
`;

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: ${({ theme }) => (theme as any).color.ui4};
  font-size: 13px;
  text-align: center;
  padding: 20px 16px;
  line-height: 1.6;
`;
